import type { ProductComparisonRowDto } from '../services/api'

export type ComparisonResultTone = 'pass' | 'fail' | 'missing' | 'review' | 'accepted'

export type ComparisonUiResult = {
  label: string
  color: string
  kingspanBg: string
  distributorBg: string
  tone: ComparisonResultTone
}

/** Maps API reviewStatus + presence flags to UI label/colours (single source of truth). */
export function resolveComparisonUiResult(row: ProductComparisonRowDto): ComparisonUiResult {
  const mode = row.compareMode ? ` (${row.compareMode})` : ''
  const status = (row.humanReviewStatus ?? row.reviewStatus ?? '').trim()

  if (status === 'Finding' || row.distributorStatus === 'Conflict') {
    return {
      label: `Failed${mode}`,
      color: '#dc3545',
      kingspanBg: '#ffffff',
      distributorBg: '#fde2e1',
      tone: 'fail',
    }
  }

  if (row.kingspanStatus === 'Missing' || row.distributorStatus === 'Missing') {
    if (status === 'Accepted') {
      return {
        label: `Accepted${mode}`,
        color: '#0d6efd',
        kingspanBg: '#fff3cd',
        distributorBg: '#e7f1ff',
        tone: 'accepted',
      }
    }
    return {
      label: `Missing${mode}`,
      color: '#b58100',
      kingspanBg: '#fff3cd',
      distributorBg: '#f3f4f6',
      tone: 'missing',
    }
  }

  if (status === 'Pass') {
    return {
      label: `Passed${mode}`,
      color: '#198754',
      kingspanBg: '#ffffff',
      distributorBg: '#e7f5e9',
      tone: 'pass',
    }
  }

  if (status === 'Accepted') {
    return {
      label: `Accepted${mode}`,
      color: '#0d6efd',
      kingspanBg: '#ffffff',
      distributorBg: '#e7f1ff',
      tone: 'accepted',
    }
  }

  if (status === 'NeedsReview' || status === 'Checked') {
    return {
      label: `Needs review${mode}`,
      color: '#b58100',
      kingspanBg: '#ffffff',
      distributorBg: '#fff8e1',
      tone: 'review',
    }
  }

  // Unknown / legacy rows: do not default to pass.
  return {
    label: `Needs review${mode}`,
    color: '#b58100',
    kingspanBg: '#ffffff',
    distributorBg: '#fff8e1',
    tone: 'review',
  }
}

export function comparisonRowNeedsAttention(row: ProductComparisonRowDto): boolean {
  const tone = resolveComparisonUiResult(row).tone
  return tone === 'fail' || tone === 'review' || tone === 'missing'
}
